<template>
    <span
        :uk-tooltip="
            disabled
                ? 'title:' + $t('x.module.disabled.hint', {x: $t('swifter')})
                : ''
        "
        :class="disabled ? 'module-disabled-struct' : ''">
        <span
            :class="
                disabled ? 'module-disabled uk-height-1-1' : 'uk-height-1-1'
            ">
            <Card
                :title="$t(title)"
                is-dark
                mode="default"
                class="uk-height-1-1">
                <Description
                    v-if="description"
                    :text="$t(description, {link: getBaseLink})" />

                <div v-if="alreadyRegistered">
                    <InfoBox mode="success">
                        <Translate
                            translation-key="device.already.registered" />
                    </InfoBox>

                    <div class="uk-margin-medium-top" />
                    <Button
                        is-large
                        is-max
                        mode="danger"
                        has-action
                        :action="deleteSelf">
                        {{
                            $t('x.delete', {
                                x: $t('device'),
                            })
                        }}
                    </Button>
                </div>
                <div v-else>
                    <span
                        v-for="(inputField, key) in inputFields"
                        :key="inputField.name">
                        <TextField
                            v-model="inputField.value"
                            :autocomplete="inputField.autocomplete"
                            :autofocus="inputField.autofocus"
                            :field-key="key"
                            :hasPasswordMeter="inputField.hasPasswordMeter"
                            :icon="inputField.icon"
                            :is-optional="inputField.optional"
                            :label="inputField.label"
                            :name="inputField.name"
                            :options="inputField.options"
                            :rules="inputField.rules"
                            :steps="inputField.steps"
                            :type="inputField.type"
                            :on-enter="actionRegister"
                            :validate="validationTrigger"
                            @setValid="setValid" />

                        <div
                            v-if="key < inputFields.length - 1"
                            class="uk-margin-bottom" />
                    </span>

                    <div class="uk-margin-medium-bottom" />
                    <Button
                        :action="actionRegister"
                        :is-loading="loading"
                        :text="$t('device.activate')"
                        has-action
                        is-large
                        is-max
                        mode="primary" />
                </div>
                <div v-if="error" class="uk-margin-medium-bottom" />
                <ErrorMessage :error="error" />

                <Modal
                    :id="modalIdDelete"
                    :title="
                        $t('x.delete', {
                            x: $t('device'),
                        })
                    ">
                    <div class="uk-text-center" v-if="selected">
                        <Translate
                            translation-key="transfer.device.delete.now.message"
                            :parameter="{x: selected.name}" />
                    </div>

                    <div
                        class="uk-flex uk-flex-center uk-margin-medium-top"
                        uk-grid>
                        <div>
                            <Button
                                :text="$t('cancel')"
                                class="uk-modal-close"
                                is-large
                                is-max
                                is-min-width
                                mode="default" />
                        </div>
                        <div>
                            <Button
                                :action="actionDelete"
                                :is-loading="deleteLoading"
                                :text="
                                    $t('x.delete', {
                                        x: $t('device'),
                                    })
                                "
                                has-action
                                is-large
                                is-max
                                is-min-width
                                mode="danger" />
                        </div>
                    </div>

                    <div v-if="deleteError" class="uk-margin-medium-bottom" />
                    <ErrorMessage :error="deleteError" />
                </Modal>

                <div v-if="tokens && tokens.length > 0">
                    <hr class="uk-hr uk-margin-medium-top" />

                    <LabelWithError isLabelLarge :label="$t('devices')" />

                    <div class="uk-grid uk-child-width-1-1" uk-grid>
                        <div
                            v-for="(token, key) in tokens"
                            :key="'transferfile' + key">
                            <div class="uk-grid uk-grid-small" uk-grid>
                                <div
                                    class="uk-width-expand uk-flex-middle uk-flex">
                                    {{ token.name }}
                                </div>
                                <div
                                    class="uk-width-expand uk-flex-middle uk-flex">
                                    <span
                                        v-if="token.lastActiveAt"
                                        :uk-tooltip="
                                            'title:' +
                                            $t('lastActiveAtClock', {
                                                x: getFormattedDateTimeByDateString(
                                                    token.lastActiveAt
                                                ),
                                            })
                                        ">
                                        <Icon icon="bolt" />
                                        <Translate
                                            translation-key="activeAgo"
                                            :parameter="{
                                                x: getAgoDescription(
                                                    token.lastActiveAt
                                                ),
                                            }" />
                                    </span>
                                    <span v-else>
                                        <Icon icon="close" />
                                        <Translate
                                            translation-key="noActivity" />
                                    </span>
                                </div>
                                <div
                                    class="uk-width-auto uk-flex-middle uk-flex uk-text-center">
                                    <span
                                        :uk-tooltip="
                                            'title:' +
                                            $t('createdAtClock', {
                                                x: getFormattedDateTimeByDateString(
                                                    token.createdAt
                                                ),
                                            })
                                        ">
                                        <Icon icon="clock" />
                                    </span>
                                </div>

                                <div class="uk-width-auto">
                                    <span class="uk-margin-small-right" />
                                    <span
                                        :uk-tooltip="
                                            $t('x.delete', {x: $t('device')})
                                        "
                                        v-on:click="deleteAction(token)">
                                        <Button is-round mode="danger">
                                            <Icon icon="trash" />
                                        </Button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </span>
    </span>
</template>

<script>
import Description from '@/components/Description.vue';
import Button from '@/components/generic/Button.vue';
import Card from '@/components/generic/Card.vue';
import ErrorMessage from '@/components/generic/ErrorMessage.vue';
import axios from 'axios';
import TextField from '@/components/generic/TextField.vue';
import LabelWithError from '@/components/generic/LabelWithError.vue';
import Icon from '@/components/generic/Icon.vue';
import Translate from '@/components/Translate.vue';
import InfoBox from '@/components/generic/InfoBox.vue';
import Modal from '@/components/generic/Modal.vue';
import UIkit from 'uikit';

export default {
    name: 'SwifterTokenForm',
    components: {
        Modal,
        InfoBox,
        Translate,
        Icon,
        LabelWithError,
        TextField,
        ErrorMessage,
        Card,
        Button,
        Description,
    },
    props: {
        text: String,
        disabled: Boolean,
        tokens: Array,
        swifterDomain: String,
    },
    data() {
        return {
            title: 'swifter.device.activate',
            description: 'swifter.device.activate.message',

            loading: false,
            success: false,
            error: null,

            validationTrigger: 0,
            inputFields: [
                {
                    value: '',
                    valid: false,

                    name: 'name',
                    label: 'name',
                    icon: 'hashtag',
                    rules: 'required|max:320',
                },
            ],

            alreadyRegistered: false,

            // delete
            selected: null,
            modalIdDelete: 'modal-transfer-device-delete',
            deleteLoading: false,
            deleteSuccess: false,
            deleteError: null,

            swiftTokenKey: 'swift_token',
            swiftID: 'swift_id',
        };
    },
    created() {
        this.validateToken();
    },
    computed: {
        getBaseLink() {
            let link =
                window.location.origin + '/' + this.$i18n.locale + '/swift';

            if (this.swifterDomain) {
                link =
                    'https://' +
                    this.swifterDomain +
                    '/' +
                    this.$i18n.locale +
                    '/swift';
            }

            return link;
        },
    },
    methods: {
        deleteSelf() {
            this.deleteAction({
                id: localStorage.getItem(this.swiftID),
                token: localStorage.getItem(this.swiftTokenKey),
            });
        },
        deleteAction(token) {
            this.selected = token;
            UIkit.modal('#' + this.modalIdDelete).show();
        },
        setValid(valid, fieldKey) {
            this.success = false;
            this.inputFields[fieldKey].valid = valid;
        },
        validateToken() {
            const token = localStorage.getItem(this.swiftTokenKey);
            if (!token) {
                return;
            }

            let http = axios.create();
            http.head(this.apiUrl + '/transfers', {
                headers: {
                    token: token,
                },
            })
                .then(() => {
                    this.alreadyRegistered = true;
                })
                .catch(() => {
                    this.alreadyRegistered = false;
                    localStorage.removeItem(this.swiftTokenKey);
                    localStorage.removeItem(this.swiftID);
                });
        },
        actionDelete() {
            this.deleteLoading = true;

            let http = axios.create();
            http.delete(
                this.apiUrl + '/manage-transfers/tokens/' + this.selected.id
            )
                .then(() => {
                    UIkit.modal('#' + this.modalIdDelete).hide();

                    this.deleteError = null;
                    this.deleteSuccess = true;
                    this.deleteLoading = false;

                    const swiftID = localStorage.getItem(this.swiftID);
                    if (this.selected.id === swiftID) {
                        this.alreadyRegistered = false;
                        localStorage.removeItem(this.swiftTokenKey);
                        localStorage.removeItem(this.swiftID);
                    }

                    this.$emit('updated');
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.deleteError = e;
                    this.deleteSuccess = false;
                    this.deleteLoading = false;
                });
        },
        actionRegister() {
            this.validationTrigger++;

            for (let i = 0, j = this.inputFields.length; i < j; i++) {
                if (!this.inputFields[i].valid) {
                    return;
                }
            }
            this.loading = true;

            let http = axios.create();
            http.post(this.apiUrl + '/manage-transfers/tokens', {
                name: this.inputFields[0].value,
            })
                .then((response) => {
                    this.error = null;
                    this.success = true;
                    this.loading = false;

                    localStorage.setItem(this.swiftID, response.data.id);
                    localStorage.setItem(
                        this.swiftTokenKey,
                        response.data.token
                    );
                    this.alreadyRegistered = true;

                    for (let i = 0, j = this.inputFields.length; i < j; i++) {
                        this.inputFields[i].valid = false;
                        this.inputFields[i].value = '';
                    }

                    this.$emit('updated');
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.error = e;
                    this.success = false;
                    this.loading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped></style>
